import { renderPageShell } from '../lib/page-shell';
import { workerKnockoutQueueUrl, workerKnockoutSocketUrl } from '../lib/worker-config';
import { fetchMe, mintConnectionTicket } from '../lib/session';
import { attachKnockoutSession } from '../lib/knockout-session';
import type { RouteHandler } from '../lib/router';

export const renderKnockout: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'play', {
    title: 'KNOCKOUT',
    subtitle: 'Any size lobby, up to 50 players. Growing eliminations each round. Last one standing wins.',
    body: `<div id="knockout-mount"></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#knockout-mount')!;
  let queueSocket: WebSocket | null = null;
  let lobbySocket: WebSocket | null = null;
  let destroySession: (() => void) | void;
  let countdownIntervalId: number | undefined;

  function clearCountdown() {
    if (countdownIntervalId !== undefined) {
      window.clearInterval(countdownIntervalId);
      countdownIntervalId = undefined;
    }
  }

  function cleanup() {
    clearCountdown();
    if (destroySession) destroySession();
    destroySession = undefined;
    if (queueSocket) {
      queueSocket.onmessage = null;
      queueSocket.onclose = null;
      queueSocket.onerror = null;
      queueSocket.close();
      queueSocket = null;
    }
    if (lobbySocket) {
      lobbySocket.onclose = null;
      lobbySocket.close();
      lobbySocket = null;
    }
  }

  function renderSignInGate() {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Sign in required</p>
        <h2>Knockout needs an account</h2>
        <button class="button button--primary" type="button" data-action="account">Go to account</button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="account"]')?.addEventListener('click', () => navigate('account'));
  }

  /**
   * `count`/`min`/`max`/`deadlineAtMs` come from `knockout-queue.ts`'s
   * `waiting` broadcast, which every waiting socket receives live as the
   * queue fills — not just the newly-joined one (owner decision, 2026-08-11:
   * "73/100 registered" scarcity/hype from the battle-royale design doc).
   */
  function renderSearching(count?: number, min?: number, max?: number, deadlineAtMs?: number | null) {
    clearCountdown();
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Searching for a lobby…</p>
        ${count !== undefined ? `<h2>${count} of ${max ?? 50} players queued</h2><p class="round-stage__hint">Needs at least ${min ?? 4} to start.</p>` : ''}
        ${deadlineAtMs ? `<p class="knockout-queue-countdown" id="knockout-countdown"></p>` : ''}
        <button class="button button--secondary" type="button" data-action="cancel">Cancel</button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="cancel"]')?.addEventListener('click', () => {
      cleanup();
      navigate('play');
    });

    if (deadlineAtMs) {
      const countdownEl = mount.querySelector<HTMLParagraphElement>('#knockout-countdown')!;
      const tick = () => {
        const remainingSeconds = Math.max(0, Math.ceil((deadlineAtMs - Date.now()) / 1000));
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        countdownEl.textContent = `Locks in ${minutes}:${String(seconds).padStart(2, '0')}`;
      };
      tick();
      countdownIntervalId = window.setInterval(tick, 1000);
    }
  }

  function renderError(message: string) {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Error</p>
        <h2>${message}</h2>
        <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', () => {
      cleanup();
      navigate('play');
    });
  }

  async function joinLobby(code: string) {
    if (queueSocket) {
      queueSocket.onmessage = null;
      queueSocket.onclose = null;
      queueSocket = null;
    }
    // The queue socket's ticket is spent; mint a fresh one for the lobby socket.
    let connectionTicket: string;
    try {
      connectionTicket = await mintConnectionTicket();
    } catch {
      renderSignInGate();
      return;
    }
    lobbySocket = new WebSocket(workerKnockoutSocketUrl(code, connectionTicket));
    lobbySocket.onclose = () => {
      lobbySocket = null;
    };
    destroySession = attachKnockoutSession(mount, lobbySocket, {
      onExit: () => {
        cleanup();
        navigate('play');
      },
    });
  }

  async function startQueue() {
    const me = await fetchMe();
    if (!me) {
      renderSignInGate();
      return;
    }

    renderSearching();
    let connectionTicket: string;
    try {
      connectionTicket = await mintConnectionTicket();
    } catch {
      renderSignInGate();
      return;
    }
    queueSocket = new WebSocket(workerKnockoutQueueUrl(connectionTicket));
    queueSocket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as {
          type: string;
          code?: string;
          count?: number;
          min?: number;
          max?: number;
          deadlineAtMs?: number | null;
          message?: string;
        };
        if (msg.type === 'waiting') {
          renderSearching(msg.count, msg.min, msg.max, msg.deadlineAtMs);
        }
        if (msg.type === 'matched' && msg.code) {
          joinLobby(msg.code);
        }
        if (msg.type === 'queue-timeout' || msg.type === 'queue-error') {
          if (msg.type === 'queue-error') {
            renderError(msg.message ?? 'Could not start a knockout match. Please queue again.');
          }
          // queue-timeout is informational only — the queue keeps running, so
          // just fold the message into the searching screen rather than exiting.
        }
      } catch {
        // ignore malformed frames
      }
    };
    queueSocket.onerror = () => renderError('Could not reach the knockout queue.');
  }

  startQueue();

  return () => cleanup();
};
